import { WebViewerInstance } from '@pdftron/webviewer';
import { useState, useEffect, useRef } from 'react';

type UseInitialLeftPanelStateProps = {
  instance: WebViewerInstance | null;
};

export const useInitialLeftPanelState = ({
  instance,
}: UseInitialLeftPanelStateProps) => {
  const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(false);
  const [hasLoadedAnyDoc, setHasLoadedAnyDoc] = useState(false);

  // Ref to store the timeout ID for debouncing
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // handle document loaded event to open or close the left panel
  useEffect(() => {
    if (!instance) return;

    // check for count of pages, and if greater than one, open left panel
    // NOTE: we handle this here and not on annotations loaded event, because
    //  this will happen prior to a render, saving a unit of work for the
    //  webviewer to have to render twice, avoiding choppiness
    // NOTE: this is when the document is loaded into memory but has yet to render
    //  docs: https://docs.apryse.com/documentation/web/guides/events/loading-events/
    instance.Core.documentViewer.addEventListener(
      'documentLoaded.leftPanelState',
      () => {
        // behavior will change based on if anything has loaded yet
        if (hasLoadedAnyDoc) {
          // the leftPanel will close by default when updating to a new document
          if (isLeftPanelOpen) {
            instance.UI.openElements(['leftPanel']);
          }
        } else {
          // if no docs have yet to be loaded, open the left panel by default
          const document = instance.Core.documentViewer.getDocument();

          if (document.getPageCount() > 1) {
            instance.UI.openElements(['leftPanel']);
          } else {
            instance.UI.closeElements(['leftPanel']);
          }
          setHasLoadedAnyDoc(true);
        }
      },
    );

    return () => {
      instance?.Core.documentViewer.removeEventListener(
        'documentLoaded.leftPanelState',
      );
    };
  }, [hasLoadedAnyDoc, instance, isLeftPanelOpen]);

  // handle manual closing of the left and right panels to update local state
  // NOTE: this is debounced because the leftPanel closes by default when unloading
  //  a document, triggering the visibilityChanged event right before the documentUnloaded event
  useEffect(() => {
    if (!instance) return;

    instance.Core.documentViewer.addEventListener('documentUnloaded', () => {
      // If document is unloaded within 100ms, clear the visibility change debounce
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
    });

    instance.UI.addEventListener('visibilityChanged', (e) => {
      if (e?.detail?.element == 'leftPanel') {
        // Start debounced update of local state
        debounceTimeoutRef.current = setTimeout(() => {
          setIsLeftPanelOpen(e.detail.isVisible);
          // Clear the timeout reference after the debounce delay
          debounceTimeoutRef.current = null;
        }, 100);
      }
    });
  }, [instance]);
};
